import { REQUIRED, t } from "../types.ts";
import type {
  ChecklistItem,
  ChecklistSectionConfig,
  TranslatedText,
} from "../types.ts";

function item(
  requirement: TranslatedText,
  required: TranslatedText,
  details: TranslatedText,
  description: TranslatedText = t("", "", ""),
): ChecklistItem {
  return { requirement, required, details, description };
}

export const ai: ChecklistSectionConfig = {
  id: "ai",
  gradient: "from-indigo-500 to-indigo-600",
  hoverBg: "indigo-400",
  title: t("AI", "MI", "IA"),
  tagline: t(
    "Adding decision-making and generation capabilities on top of automation",
    "Lēmumu pieņemšanas un ģenerēšanas spēju pievienošana automatizācijai",
    "Aggiungere capacità decisionali e generative sopra l'automazione",
  ),
  subsections: [
    {
      title: t(
        "4.1. AI-powered System Design",
        "4.1. MI Vadītu Sistēmu Projektēšana",
        "4.1. Progettazione di Sistemi Basati su IA",
      ),
      items: [
        item(
          t("AI Suitability", "MI Piemērotība", "Idoneità all'IA"),
          REQUIRED,
          t(
            "Does the task require processing unstructured data or complex pattern recognition where rules-based logic falls short?",
            "Vai uzdevums prasa nestrukturētu datu apstrādi vai sarežģītu modeļu atpazīšanu, kur uz noteikumiem balstīta loģika ir nepietiekama?",
            "Il task richiede l'elaborazione di dati non strutturati o il riconoscimento di pattern complessi dove la logica basata su regole è insufficiente?",
          ),
          t(
            "e.g., free text classification, document parsing, image analysis — if a simple if/else works, use automation instead.",
            "piem., brīvā teksta klasifikācija, dokumentu apstrāde, attēlu analīze — ja vienkārša if/else loģika darbojas, izmantojiet automatizāciju.",
            "es., classificazione del testo libero, parsing di documenti, analisi di immagini — se un semplice if/else funziona, usare l'automazione.",
          ),
        ),
        item(
          t(
            "Specialised Modality",
            "Specializētais Datu Veids",
            "Modalità Specializzata",
          ),
          REQUIRED,
          t(
            "What type of data does the AI need to process or generate?",
            "Kāda veida datus MI ir jāapstrādā vai jāģenerē?",
            "Che tipo di dati l'IA deve elaborare o generare?",
          ),
          t(
            "Text only · Media (Image, Video, Audio) · Code generation · Multi-language NLP.",
            "Tikai teksts · Multivide (Attēls, Video, Audio) · Koda ģenerēšana · Daudzvalodu NLP.",
            "Solo testo · Media (Immagine, Video, Audio) · Generazione di codice · NLP multilingue.",
          ),
        ),
        item(
          t(
            "Model Agency & Autonomy",
            "Modeļa Rīcībspēja un Autonomija",
            "Autonomia e Agenzia del Modello",
          ),
          REQUIRED,
          t(
            "Does the AI act as an autonomous agent or as a copilot/assistant?",
            "Vai MI darbojas kā autonoms aģents vai kā kopilots/asistents?",
            "L'IA agisce come agente autonomo o come copilota/assistente?",
          ),
          t(
            "Agentic: AI takes actions autonomously (higher risk). Copilot: AI suggests, human decides (lower risk). Match autonomy to risk tolerance.",
            "Autonoms režīms: MI veic darbības patstāvīgi (augstāks risks). Kopilots: MI iesaka, cilvēks izlemj (zemāks risks). Pielāgojiet autonomiju riska tolerancei.",
            "Agentico: l'IA agisce autonomamente (rischio maggiore). Copilota: l'IA suggerisce, l'umano decide (rischio minore). Adeguare l'autonomia alla tolleranza al rischio.",
          ),
        ),
        item(
          t(
            "Cost-to-Benefit",
            "Izmaksu un Ieguvumu Analīze",
            "Analisi Costi-Benefici",
          ),
          REQUIRED,
          t(
            "Does the cost of tokens/compute/subscriptions justify the manual hours saved?",
            "Vai tokenu/skaitļošanas/abonementa izmaksas attaisno ietaupītās manuālās stundas?",
            "Il costo di token/calcolo/abbonamenti giustifica le ore manuali risparmiate?",
          ),
          t(
            "Calculate monthly token cost vs. FTE hours saved; include latency impact on user experience.",
            "Aprēķiniet ikmēneša tokenu izmaksas salīdzinājumā ar ietaupītajām FTE stundām; iekļaujiet latentuma ietekmi uz lietotāja pieredzi.",
            "Calcolare il costo mensile dei token vs. ore FTE risparmiate; includere l'impatto della latenza sull'esperienza utente.",
          ),
        ),
      ],
    },
    {
      title: t(
        "4.2. AI Principles",
        "4.2. MI Principi",
        "4.2. Principi dell'IA",
      ),
      items: [
        item(
          t("Data Privacy", "Datu Privātums", "Privacy dei Dati"),
          REQUIRED,
          t(
            "Is data sent to the AI isolated from public model training? Is training consent disabled?",
            "Vai MI nosūtītie dati ir izolēti no publiskās modeļa apmācības? Vai apmācības piekrišana ir atspējota?",
            "I dati inviati all'IA sono isolati dall'addestramento del modello pubblico? Il consenso all'addestramento è disabilitato?",
          ),
          t(
            "Use enterprise tiers (OpenAI, Gemini, Claude) that guarantee no training on your data; verify via DPA.",
            "Izmantojiet uzņēmumu līmeņa pakalpojumus (OpenAI, Gemini, Claude), kas garantē, ka jūsu dati netiks izmantoti apmācībai; pārbaudiet ar DPA.",
            "Usare livelli enterprise (OpenAI, Gemini, Claude) che garantiscono nessun addestramento sui propri dati; verificare tramite DPA.",
          ),
        ),
        item(
          t(
            "Ethics Accountability & Liability",
            "Ētika, Pārskatatbildība un Juridiskā Atbildība",
            "Etica, Responsabilità e Responsabilità Legale",
          ),
          REQUIRED,
          t(
            "Can we explain the AI's decision? Who is liable for mistakes? Is the user informed of AI interaction?",
            "Vai mēs varam izskaidrot MI lēmumu? Kurš ir atbildīgs par kļūdām? Vai lietotājs ir informēts par MI mijiedarbību?",
            "Possiamo spiegare la decisione dell'IA? Chi è responsabile degli errori? L'utente è informato dell'interazione con l'IA?",
          ),
          t(
            "Check for bias in outputs; define liability in contracts; display AI-disclosure notices to end users as required by the EU AI Act.",
            "Pārbaudiet neobjektivitāti izvades rezultātos; definējiet atbildību līgumos; rādiet MI izpaušanas paziņojumus gala lietotājiem saskaņā ar ES MI aktu.",
            "Verificare i bias negli output; definire la responsabilità nei contratti; mostrare avvisi di divulgazione IA agli utenti finali come richiesto dall'AI Act UE.",
          ),
        ),
      ],
    },
  ],
};
